/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { Button } from '@mui/material';
import { CellType } from '@grapecity/wijmo.grid';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import {
  SubTitleLayout,
  SubTitleGroup,
  ControlBtnGroup,
  GlobalBtnGroup,
} from 'components/layouts/ContentLayout';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
  SearchButtonWrap,
} from 'components/layouts/SearchBox';
import { ComboBox } from 'components/selects/ComboBox';
import { IconButton } from 'components/buttons/IconSVG';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { MultiComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Code } from 'models/common/CommonCode';
import {
  checkAvailableProjectManager,
  findProject,
  getSetupProjectList,
  saveProjectBookmark,
} from 'apis/setup/SetupProject';
import {
  DefaultNoneNumberCellTemplate,
  DefaultZeroNumberCellTemplate,
} from 'components/grids/NumberCellRenderer';
import { SetupProject, SetupProjectCondition } from 'models/setup/SetupProject';
import { useCommonModal } from 'hooks/useCommonModal';
import useSessionStore from 'stores/useSessionStore';
import { CommonUtil } from 'utils/CommonUtil';
import UserMultiModal from 'components/modals/common/UserMultiModal';
import { ManagementMode } from 'models/common/Common';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { useLoading } from 'components/process/Loading';
import SetupManagerPopUp from './common/SetupManagerPopUp';
import { hasRole } from 'utils/SessionUtil';
import { BmkAtcTpCd, BmkTgtTpCd } from '../../models/common/Bookmark';
import { saveCondBookmarks } from '../../apis/common/Bookmark';
import { RoleCode } from '../../models/common/RoleCode';

interface PageLocationState {
  elmPrjId?: string;
}

const SetupProjectPage = () => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const navigate = useNavigate();
  const { userId, elmPrjId } = useSessionStore();

  const [searchParams] = useSearchParams();

  const { state } = useLocation();
  const [locationState, setLocationState] = useState<PageLocationState>(useLocation().state);

  const gridRef = useRef<any>();
  const [flexRef, setFlexRef] = useState<any>();

  const [rowData, setRowData] = useState<SetupProject[]>([]);

  const [userModalMode, setUserModalMode] = useState<string>('');

  const initCondition = {
    bildPlntCds: [],
    prdnProcCds: [],
    dtalProcCds: [],
    elmPrjId: '',
    elmPrjNm: '',
    suHqMgrId: '',
    suHqMgrNm: '',
    dataInsUserId: '',
    dataInsUserNm: '',
    prjDivsCd: '',
    prjDivsCds: [],
    elmPrjStatCd: '',
    prjOrgTpCds: [], //조직유형
    cellFertTpCds: [], //유형
  };

  const [setupProjectCondition, setSetupProjectCondition] = useState<SetupProjectCondition>({
    ...initCondition,
  });

  const [bildPlntCode, setBildPlntCode] = useState<Code[]>([]);
  const [prjDivsCd, setPrjDivsCd] = useState<Code[]>([]);
  const [elmPrjStatCd, setElmPrjStatCd] = useState<Code[]>([]);
  const [prjOrgTpCode, setPrjOrgTpCd] = useState<Code[]>([]);
  const [cellFertTpCode, setCellFertTpCd] = useState<Code[]>([]);

  const [prjMasterData, setPrjMasterData] = useState<SetupProject[]>([]);

  const [hasAuth, setHasAuth] = useState<boolean>(false);
  const [hasModifyAuth, setHasModifyAuth] = useState<boolean>(false);

  const [isOpenUserModal, setOpenUserModal] = useState<boolean>(false);

  const [setupManagerCondition, setSetupManagerCondition] = useState<any>({});
  const [isOpenSetupManagerPopup, setOpenSetupManagerPopup] = useState<boolean>(false);

  const isCreateProjectAuth = useMemo(() => {
    if (hasRole('FA_MANAGER') || hasRole('FA_USER') || hasRole('FA_USER_COP')) {
      return true;
    }
    return false;
  }, []);

  const searchUserRoles = useMemo(() => {
    if ('searchDataInsUserId' === userModalMode) {
      // 등록자인 경우 FA관리자 포함
      return [RoleCode.FA_USER, RoleCode.FA_USER_COP, RoleCode.FA_MANAGER];
    }
    return [RoleCode.FA_USER, RoleCode.FA_USER_COP];
  }, [isOpenUserModal, userModalMode]);

  // 수정권한체크 -> FA 담당자(본사/법인), FA_MANAGER
  /* useEffect(() => {
    (async () => {
      let auth = false;
      if (hasRole('FA_MANAGER')) {
        auth = true;
      }
      await checkAvailableProjectManager(elmPrjId, 'ALL').then((res) => (auth = res));
      setHasModifyAuth(auth);
    })();
  }, []);*/

  const optionsPrjMaster = useMemo(() => {
    return (prjMasterData || [])
      .filter((o) =>
        (setupProjectCondition?.bildPlntCds || []).length > 0
          ? setupProjectCondition.bildPlntCds.includes(o.bildPlntCd)
          : true
      )
      .reduce(
        (acc, cur) => [
          ...acc,
          {
            cmnCd: cur.elmPrjId,
            cmnCdNm: cur.elmPrjNm,
          } as Code,
        ],
        [] as Code[]
      );
  }, [prjMasterData, setupProjectCondition?.bildPlntCds]);

  useEffect(() => {
    initCodes();

    if (hasRole('ADM') || hasRole('FA_MANAGER') || hasRole('FA_USER')) {
      setHasAuth(true);
    }
    if (elmPrjId) {
      handleOnChange('elmPrjId', elmPrjId);
    }
    btnSearch(elmPrjId);
  }, []);

  const initCodes = async () => {
    const bildPlntCode = await getCommonCodeNames('FA_SU_PLNT_CD');
    const prjDivsCd = await getCommonCodeNames('PRJ_DIVS_CD');
    const elmPrjStatCd = await getCommonCodeNames('ELM_PRJ_STAT_CD');
    const prjOrgTpCd = await getCommonCodeNames('PRJ_ORG_TP_CD');
    const cellFertTpCd = await getCommonCodeNames('CELL_FERT_TP_CD');

    setBildPlntCode(bildPlntCode);
    setPrjDivsCd(prjDivsCd);
    setElmPrjStatCd(elmPrjStatCd);
    setPrjOrgTpCd(prjOrgTpCd);
    setCellFertTpCd(cellFertTpCd);

    findProject().then((result: SetupProject[]) => {
      if (result !== null) {
        setPrjMasterData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  useEffect(() => {
    // setSetupProjectCondition((prev) => ({
    //   ...prev,
    //   // elmPrjId: locationState?.elmPrjId || prev?.elmPrjId || searchParams?.get('elmPrjId') || '',
    // }));
    if (locationState?.elmPrjId) {
      btnSearch();
    }
  }, [locationState?.elmPrjId, searchParams?.get('elmPrjId')]);

  useEffect(() => {
    if (state?.elmPrjId) {
      setLocationState(state);
    }
  }, [state]);

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'elmPrjId',
        visible: false,
      },
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        align: 'center',
        minWidth: 40,
        width: 40,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'bmkYn',
        header: String(t('mp.grid.요약 반영 여부', '요약 반영 여부')),
        width: 90,
        align: 'center',
        cssClass: 'WijmoBookmark',
        isReadOnly: true,
        cellTemplate: (ctx) =>
          `<button class="${ctx.value === 'Y' ? 'detail' : ''}" id="bmkBtn"></button>`,
      },
      {
        binding: 'elmPrjTpCdNm',
        header: String(t('setup.label.조직유형', '조직유형')),
        minWidth: 80,
        width: 130,
        align: 'center',
      },
      {
        binding: 'bildPlntCdNm',
        header: String(t('setup.label.플랜트', '플랜트')),
        minWidth: 80,
        width: 130,
        align: 'center',
      },
      {
        binding: 'elmPrjNm',
        header: String(t('setup.label.프로젝트명', '프로젝트명')),
        width: '*',
        minWidth: 210,
        align: 'left',
        cellTemplate: CellMaker.makeLink({
          text: '<span>${item.elmPrjNm}</span>',
          click: (e, ctx) => {
            moveToPage(ctx.item);
          },
        }),
      },
      {
        binding: 'prdnProcCd',
        visible: false,
      },
      {
        binding: 'cellFertTpCdNm',
        header: String(t('setup.label.유형', '유형')),
        minWidth: 80,
        width: 130,
        align: 'center',
      },
      {
        binding: 'suHqMgrNm',
        header: String(t('setup.label.FA 담당자(본사)', 'FA 담당자(본사)')),
        minWidth: 100,
        width: 127,
        align: 'left',
        cssClass: 'WijmoFind',
        isRequired: false,
        cellTemplate: (params) => `
            <span>${params.value || ''}</span>
            <Button /> 
          `,
      },
      {
        binding: 'suCopMgrNm',
        header: String(t('setup.label.FA 담당자(법인)', 'FA 담당자(법인)')),
        minWidth: 100,
        width: 127,
        align: 'left',
        cssClass: 'WijmoFind',
        isRequired: false,
        cellTemplate: (params) => `
            <span>${params.value || ''}</span>
            <Button /> 
          `,
      },
      {
        binding: 'ivesStDt',
        header: String(t('setup.label.프로젝트 기간', '프로젝트 기간')),
        minWidth: 100,
        width: 185,
        align: 'center',
        cellTemplate: (params) =>
          CommonUtil.formatPeriod(`${params.item.ivesStDt}`, `${params.item.ivesEndDt}`),
      },
      {
        binding: 'elmPrjStatCdNm',
        header: String(t('setup.label.상태', '상태')),
        minWidth: 80,
        width: 89,
        align: 'center',
        cssClass: 'WijmoTag',
        cellTemplate: (item) => {
          if (item.item.elmPrjStatCd === 'PRJ_CPLT') {
            return `<span class="yellow">${item.value}</span>`;
          } else if (item.item.elmPrjStatCd === 'REG_CPLT') {
            return `<span class="green">${item.value}</span>`;
          } else if (item.item.elmPrjStatCd === 'SAVE') {
            return `<span class="blue">${item.value}</span>`;
          } else {
            return `<span class="grey">${item.value}</span>`;
          }
        },
      },
      {
        binding: 'prjDivsCdNm',
        header: String(t('setup.label.구분', '구분')),
        minWidth: 90,
        width: 90,
        align: 'center',
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('com.label.등록자', '등록자')),
        minWidth: 90,
        width: 100,
        align: 'center',
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.등록일', '등록일')),
        minWidth: 100,
        width: 120,
        align: 'center',
      },
      {
        header: String(t('setup.label.라인수', '라인수')),
        binding: 'eltrEqpQty',
        minWidth: 95,
        width: 100,
        align: 'center',
        cellTemplate: (params) => {
          return `${
            params.item.eltrEqpQty ? Number(params.item.eltrEqpQty ?? 0).toLocaleString() : '0'
          } x ${
            params.item.asyEqpQty ? Number(params.item.asyEqpQty ?? 0).toLocaleString() : '0'
          } x ${params.item.mduEqpQty ? Number(params.item.mduEqpQty ?? 0).toLocaleString() : '0'}`;
        },
      },
      /*{
        header: String(t('setup.label.라인수(CELL 기준)', '라인수(CELL 기준)')),
        binding: 'asyEqpQty',
        minWidth: 110,
        width: 110,
        align: 'center',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        header: String(t('setup.label.라인수(모듈 기준)', '라인수(모듈 기준)')),
        binding: 'mduEqpQty',
        minWidth: 110,
        width: 110,
        align: 'center',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },*/
      {
        binding: 'prdnCapa',
        header: String(t('setup.label.CAPA', 'CAPA')),
        minWidth: 60,
        width: 90,
        align: 'right',
        cellTemplate: DefaultZeroNumberCellTemplate,
      },
      {
        binding: 'wbsCd',
        header: String(t('setup.label.WBS', 'WBS')),
        minWidth: 60,
        width: 90,
        align: 'center',
      },
    ];
  }, [rowData]);

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      const changeCursor = (type = 'default') => {
        const span = cell.querySelector('button');
        if (span) {
          span.style.cursor = type;
        }
      };
      // 담당자 없는 경우 담당자 팝업 미노출
      if ('suHqMgrNm' === binding && !item.suHqMgrNm) {
        changeCursor();
      }
      if ('suCopMgrNm' === binding && !item.suCopMgrNm) {
        changeCursor();
      }
    }
  });

  const onInitialized = useCallback((grid) => {
    gridRef.current = grid;
    setFlexRef(grid);

    grid.itemFormatter = onItemFormatter;
    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      if (ht.panel.cellType != 1) return;

      const item = grid.rows[ht.row].dataItem;
      if (e.target instanceof HTMLButtonElement) {
        switch (e.target.id) {
          case 'bmkBtn':
            checkAvailableProjectManager(item?.elmPrjId, 'ALL').then((res) => {
              if (res || hasRole('FA_MANAGER')) {
                item.bmkYn = item.bmkYn === null || item.bmkYn === 'N' ? 'Y' : 'N';
                saveProjectBookmark(item).then((res) => {
                  if (res.successOrNot === 'Y') {
                    gridRef.current.refresh();
                  }
                  if (res.successOrNot === 'N') {
                    openMessageBar({
                      type: 'error',
                      content: t(
                        'com.label.저장 중 오류가 발생했습니다.',
                        '저장 중 오류가 발생했습니다.'
                      ),
                    });
                  }
                });
              } else {
                openMessageBar({
                  type: 'error',
                  content: t(`setup.msg.{{prj}} : 프로젝트 권한이 없습니다.`, {
                    defaultValue: `{{prj}}: 프로젝트 권한이 없습니다.`,
                    prj: item.elmPrjNm,
                  }),
                });
              }
            });
            break;
        }
      }

      // 담당자 없는 경우 담당자 팝업 미노출
      if (ht.panel === grid.cells) {
        const binding = grid.columns[ht.col].binding;
        if ('suHqMgrNm' === binding) {
          if (item.suHqMgrNm) {
            setSetupManagerCondition({
              title: t('setup.label.FA 담당자(본사)', 'FA 담당자(본사)'),
              wktTpCd: 'SU_HQ_MGR',
              elmPrjId: item.elmPrjId,
            });
            setOpenSetupManagerPopup(true);
          }
        } else if ('suCopMgrNm' === binding) {
          if (item.suCopMgrNm) {
            setSetupManagerCondition({
              title: t('setup.label.FA 담당자(법인)', 'FA 담당자(법인)'),
              wktTpCd: 'SU_COP_MGR',
              elmPrjId: item.elmPrjId,
            });
            setOpenSetupManagerPopup(true);
          }
        }
      }
    });
  }, []);

  const handleOnChange = (name, value) => {
    setSetupProjectCondition((prev) => ({ ...prev, [name]: value }));
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      btnSearch();
    }
  };

  const btnSearch = (elmPrjId?: string) => {
    openLoading(true);
    getSetupProjectList({
      ...setupProjectCondition,
      elmPrjId: elmPrjId || setupProjectCondition?.elmPrjId,
    })
      .then((result: SetupProject[]) => {
        if (result !== null) {
          setRowData(result);
        } else {
          openMessageBar({
            type: 'error',
            content: t(
              'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
              '요청 정보 조회 중 오류가 발생했습니다.'
            ),
          });
        }
      })
      .finally(() => openLoading(false));
  };

  const moveToPage = (item: any) => {
    if (item?.elmPrjId) {
      if (item?.elmPrjStatCd === 'SAVE' && item?.dataInsUserId !== userId) {
        openMessageBar({
          type: 'warning',
          content:
            `${item?.dataInsUserNm}` +
            String(t('com.msg.님이 작성중입니다.', '님이 작성중입니다.')),
        });
        return;
      }

      const searchPageCondition = {
        mode: 'READ',
        upprMnuUrl: location.pathname,
        mnuId: `${location.pathname}/${ManagementMode.MODIFY}`,
        mnuNm: t('setup.label.프로젝트 상세', '프로젝트 상세'),
        elmPrjId: item.elmPrjId,
      };
      navigate(`/setup/project-detail?elmPrjId=${item.elmPrjId}`, {
        state: { ...searchPageCondition },
      });
    }
  };

  const btnNew = (item: any) => {
    const searchPageCondition = {
      upprMnuUrl: location.pathname,
      mnuId: `${location.pathname}/${ManagementMode.CREATE}`,
      mnuNm: t('setup.label.프로젝트 등록', '프로젝트 등록'),
      mode: 'WRITE',
    };
    navigate('/setup/project-detail/new', { state: { ...searchPageCondition } });
  };

  const btnExcelExport = () => {
    CommonUtil.exportWijmoExcelGrid(
      flexRef,
      t('setup.label.프로젝트 목록', '프로젝트 목록'),
      t('setup.label.프로젝트 List', '프로젝트 List')
    );
  };

  const callbackUserData = (callbackData) => {
    if (userModalMode === 'searchSuHqMgrId') {
      setSetupProjectCondition({
        ...setupProjectCondition,
        searchSuHqMgrId: callbackData?.userId || '',
        suHqMgrNm: callbackData?.empNm || '',
      });
    } else if (userModalMode === 'searchDataInsUserId') {
      setSetupProjectCondition({
        ...setupProjectCondition,
        searchDataInsUserId: callbackData?.userId || '',
        dataInsUserNm: callbackData?.empNm || '',
      });
    }
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{String(t('setup.label.조직유형', '조직유형'))}</label>
                <MultiComboBox
                  id="prjOrgTpCd"
                  options={prjOrgTpCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={setupProjectCondition?.prjOrgTpCds || []}
                  onChange={(value) => {
                    handleOnChange('prjOrgTpCds', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.플랜트', '플랜트'))}</label>
                <MultiComboBox
                  id="bildPlntCd"
                  options={bildPlntCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={setupProjectCondition?.bildPlntCds || []}
                  onChange={(value) => {
                    handleOnChange('bildPlntCds', value);
                    if (
                      !(prjMasterData || []).filter(
                        (o) =>
                          setupProjectCondition?.elmPrjId === o.elmPrjId &&
                          ((value || []).length < 1 || (value || []).includes(o.bildPlntCd || ''))
                      ).length
                    ) {
                      handleOnChange('elmPrjId', '');
                    }
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.프로젝트명', '프로젝트명'))}</label>
                <ComboBox
                  placeholder={String(t('com.label.전체', '전체'))}
                  options={optionsPrjMaster}
                  defaultValue={setupProjectCondition?.elmPrjId}
                  onChange={(value) => {
                    handleOnChange('elmPrjId', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.구분', '구분'))}</label>
                <ComboBox
                  options={prjDivsCd}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={setupProjectCondition.prjDivsCd}
                  msgError={String(t('com.msg.구분을 선택해 주세요.', '구분을 선택해 주세요.'))}
                  onChange={(value) => {
                    setSetupProjectCondition({
                      ...setupProjectCondition,
                      prjDivsCd: value,
                    });
                  }}
                />
              </SearchCols>
            </SearchRows>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>{String(t('setup.label.유형', '유형'))}</label>
                <MultiComboBox
                  id="cellFertTpCd"
                  options={cellFertTpCode}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={setupProjectCondition?.cellFertTpCds || []}
                  onChange={(value) => {
                    handleOnChange('cellFertTpCds', value);
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.상태', '상태')}</label>
                <ComboBox
                  options={elmPrjStatCd}
                  placeholder={String(t('com.label.전체', '전체'))}
                  defaultValue={setupProjectCondition.elmPrjStatCd}
                  msgError={String(t('com.msg.상태를 선택해 주세요.', '상태를 선택해 주세요.'))}
                  onChange={(value) => {
                    setSetupProjectCondition({
                      ...setupProjectCondition,
                      elmPrjStatCd: value,
                    });
                  }}
                />
              </SearchCols>
              <SearchCols>
                <label>{String(t('setup.label.담당자', '담당자'))}</label>
                <CustomInputWithSearch
                  name="userId"
                  className="find"
                  value={setupProjectCondition.suHqMgrNm || ''}
                  defaultValue={setupProjectCondition.suHqMgrNm || ''}
                  placeholder={String(
                    t('com.label.담당자를 선택해 주세요.', '담당자를 선택해 주세요.')
                  )}
                  onSearchClick={(e) => {
                    setUserModalMode('searchSuHqMgrId');
                    setOpenUserModal(true);
                  }}
                  onKeyDown={onKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>{t('setup.label.등록자', '등록자')}</label>
                <CustomInputWithSearch
                  name="userId"
                  className="find"
                  value={setupProjectCondition.dataInsUserNm || ''}
                  defaultValue={setupProjectCondition.dataInsUserNm || ''}
                  placeholder={String(
                    t('com.label.등록자를 선택해 주세요.', '등록자를 선택해 주세요.')
                  )}
                  onSearchClick={(e) => {
                    setUserModalMode('searchDataInsUserId');
                    setOpenUserModal(true);
                  }}
                  onKeyDown={onKeyDown}
                />
              </SearchCols>
            </SearchRows>
          </InputBox>
          <SearchButtonWrap>
            <SearchBoxButtons
              defaultCondition={initCondition}
              setReset={setSetupProjectCondition}
              onSearch={() => btnSearch()}
            />
          </SearchButtonWrap>
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.프로젝트 목록', '프로젝트 목록')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={btnExcelExport}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => btnSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        isSelector={false}
        isReadOnly={true}
        allowPinning={false}
        initialized={onInitialized}
        height={510}
        excludeFilter={['bmkYn']}
        excludePin={['bmkYn']}
      />
      <GlobalBtnGroup>
        {isCreateProjectAuth ? (
          <Button css={IconButton.button} className="create" onClick={btnNew} disableRipple>
            {t('com.button.신규등록', '신규등록')}
          </Button>
        ) : (
          ''
        )}
      </GlobalBtnGroup>
      {isOpenUserModal && (
        <UserMultiModal
          open={isOpenUserModal}
          close={() => setOpenUserModal(false)}
          title={t('com.label.담당자 조회', '담당자 조회')}
          mode={userModalMode}
          condition={{ roles: searchUserRoles }}
          defaultUserId={setupProjectCondition?.[userModalMode]}
          isOnlyUsed={true}
          singleSelect={true}
          onCallback={(callbackData) => {
            callbackUserData(callbackData);
          }}
        />
      )}
      {isOpenSetupManagerPopup && (
        <SetupManagerPopUp
          open={isOpenSetupManagerPopup}
          close={() => setOpenSetupManagerPopup(false)}
          title={setupManagerCondition?.title}
          condition={setupManagerCondition}
        />
      )}
    </>
  );
};

export default SetupProjectPage;
